import { ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useAppSnackbar } from '@/stores/app/snackbar';
import {
  searchJobPostsForWizard,
  previewJobPostFromWizard,
  recruitJsonGetTemplates,
  createJobPostFromWizard,
} from '@/api/recruitJson';

export const useCreateJobPostWizard = defineStore('createJobPostWizard', () => {
  const { addErrorSnackbar, addMultilineErrorSnackbar } = useAppSnackbar();

  // ─── step ────────────────────────────────────────────────────────────────────
  const step = ref(1);

  // ─── step 1: form data ───────────────────────────────────────────────────────
  const formTitle = ref('');
  const formJobCode = ref('');
  const formLocation = ref('');

  // ─── step 1: search ──────────────────────────────────────────────────────────
  const searchResults = ref([]);
  const searchLoading = ref(false);

  // ─── step 1 & 2: selection and backend-composed preview ─────────────────────
  const selectedJobId = ref(null);
  const previewFields = ref(null);
  const previewLoading = ref(false);
  let previewRequestId = 0;

  // ─── step 2: templates ────────────────────────────────────────────────────────
  const templates = ref([]);
  const templatesLoading = ref(false);
  const selectedTemplateId = ref(null);

  // ─── creating ────────────────────────────────────────────────────────────────
  const createLoading = ref(false);
  const firstDraftLoading = ref(false);

  // ─── actions ─────────────────────────────────────────────────────────────────

  const searchJobPosts = async () => {
    if (!formTitle.value && !formJobCode.value) {
      searchResults.value = [];
      return;
    }
    searchLoading.value = true;
    try {
      const { jobs } = await searchJobPostsForWizard({
        title: formTitle.value,
        jobCode: formJobCode.value,
      });
      searchResults.value = jobs || [];
    } catch (err) {
      addErrorSnackbar({ text: `Job search error: ${err.message || err}` });
      throw err;
    } finally {
      searchLoading.value = false;
    }
  };

  /**
   * Fields for the preview are composed by the backend with the same code that builds the
   * new post, so the preview always matches the created result.
   */
  const refreshPreview = async () => {
    previewRequestId += 1;
    const requestId = previewRequestId;

    if (!selectedJobId.value && !selectedTemplateId.value) {
      previewFields.value = null;
      previewLoading.value = false;
      return;
    }

    previewLoading.value = true;
    try {
      const { fields } = await previewJobPostFromWizard({
        templateId: selectedTemplateId.value,
        similarJobId: selectedJobId.value,
      });
      if (requestId === previewRequestId) previewFields.value = fields;
    } catch (err) {
      if (requestId === previewRequestId) {
        previewFields.value = null;
        addErrorSnackbar({ text: `Job preview error: ${err.message || err}` });
      }
    } finally {
      if (requestId === previewRequestId) previewLoading.value = false;
    }
  };

  watch([selectedJobId, selectedTemplateId], refreshPreview);

  const selectJob = (job) => {
    selectedJobId.value = job?.id ?? null;
  };

  const goToStep2 = () => { step.value = 2; };
  const goToStep1 = () => {
    // The template is chosen in step 2, so going back starts from "no template" again.
    selectedTemplateId.value = null;
    step.value = 1;
  };

  const loadTemplates = async () => {
    templatesLoading.value = true;
    try {
      const { templates: list } = await recruitJsonGetTemplates();
      templates.value = list || [];
    } catch (err) {
      addErrorSnackbar({ text: `Templates load error: ${err.message || err}` });
      throw err;
    } finally {
      templatesLoading.value = false;
    }
  };

  const selectTemplate = (id) => {
    selectedTemplateId.value = id;
  };

  const clearTemplate = () => {
    selectedTemplateId.value = null;
  };

  const createJobPost = async (router, { writeWithFirstDraft = false } = {}) => {
    createLoading.value = true;
    if (writeWithFirstDraft) firstDraftLoading.value = true;
    try {
      const { jobId } = await createJobPostFromWizard({
        title: formTitle.value?.trim(),
        location: formLocation.value?.trim(),
        jobCode: formJobCode.value?.trim(),
        templateId: selectedTemplateId.value,
        similarJobId: selectedJobId.value,
      });
      await router.push({
        name: 'editor-view-reload',
        params: { id: jobId },
        query: writeWithFirstDraft ? { writeWithFirstDraft: true } : undefined,
      });
    } catch (err) {
      addMultilineErrorSnackbar({
        text: `An error occurred while creating this job post.
        Our support team will contact you once the issue has been resolved.
        ${err.message}`,
      });
      throw err;
    } finally {
      createLoading.value = false;
      firstDraftLoading.value = false;
    }
  };

  const reset = () => {
    step.value = 1;
    formTitle.value = '';
    formJobCode.value = '';
    formLocation.value = '';
    searchResults.value = [];
    searchLoading.value = false;
    previewFields.value = null;
    previewLoading.value = false;
    selectedJobId.value = null;
    templates.value = [];
    templatesLoading.value = false;
    selectedTemplateId.value = null;
    createLoading.value = false;
    firstDraftLoading.value = false;
  };

  return {
    // state
    step,
    formTitle,
    formJobCode,
    formLocation,
    searchResults,
    searchLoading,
    selectedJobId,
    previewFields,
    previewLoading,
    templates,
    templatesLoading,
    selectedTemplateId,
    createLoading,
    firstDraftLoading,

    // actions
    searchJobPosts,
    selectJob,
    goToStep1,
    goToStep2,
    loadTemplates,
    selectTemplate,
    clearTemplate,
    createJobPost,
    reset,
  };
});
